import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";

import { faSparkles, faTriangleExclamation, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { ROUTE_CONFIG } from "@/routes/config";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";

import { KNOWLEDGE_DB_TOUR_PARAM } from "@/pages/Private/pages/KnowledgeDb/components/KnowledgeDbTourReturnBar";

import {
	useGetOnboardingTourQuery,
	useRetryKnowledgeBaseMutation,
} from "@/pages/Private/redux/onboardingTour/onboardingTour.api";
import {
	openTour,
	selectIsTourOpen,
	selectListTourOpen,
	selectTourState,
} from "@/pages/Private/redux/onboardingTour/onboardingTour.slice";
import {
	OnboardingTourStatus,
	OnboardingTourVariant,
	TourStepId,
} from "@/pages/Private/redux/onboardingTour/schema";

import { getFailedStep, getReadyStep, hasPendingGeneration } from "./tourReadyStep";

/**
 * i18n sub-keys under `onboardingTour.readyBar` per surfaced step. The scoring
 * steps all review the same generated config, so they share one line.
 */
const READY_BAR_COPY_KEY: Partial<Record<TourStepId, string>> = {
	[TourStepId.SCORING]: "scoring",
	[TourStepId.SCORING_INTRO]: "scoring",
	[TourStepId.SCORING_SEGMENTS]: "scoring",
	[TourStepId.SCORING_ROLES]: "scoring",
	[TourStepId.SCORING_GRADING]: "scoring",
	[TourStepId.SCORING_ACTIVATE]: "scoring",
	[TourStepId.DEEP_RESEARCH]: "deepResearch",
	[TourStepId.KNOWLEDGE]: "knowledge",
};

const POLL_INTERVAL_MS = 30_000;

/**
 * sessionStorage key holding the step whose reminder the user hid. Scoped to the
 * step (not a plain flag) so a *different* step finishing still surfaces its own
 * reminder, and scoped to the session so the nudge returns on the next visit.
 */
export const TOUR_READY_BAR_DISMISSED_KEY = "tourReadyBarDismissedStep";

/**
 * Sticky ready-to-review reminder (PREC-3719 §9): while the configuration flow
 * is unfinished (ACTIVE or SKIPPED) and closed, and at least one step's
 * generation is READY, a docked bottom bar on every app surface invites the
 * user to review it — reopening the tour on that step. Polls while a generation
 * runs.
 *
 * The bar docks over the bottom of the viewport and can cover controls there, so
 * it can be dismissed — that only hides the reminder for this session, it does
 * not skip the review itself (the step stays READY and the bar returns next
 * session, or as soon as another step becomes ready).
 *
 * The same bar reports a Knowledge Base build that failed (PREC-4174) and offers
 * to run it again: until then the spinner simply stopped and nothing said why.
 */
export const TourReadyBar: FunctionComponent = () => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const { user } = useAppSelector(authSelector);
	const tourState = useAppSelector(selectTourState);
	const isTourOpen = useAppSelector(selectIsTourOpen);
	const isListTourOpen = useAppSelector(selectListTourOpen);
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const [dismissedStep, setDismissedStep] = useState(() =>
		sessionStorage.getItem(TOUR_READY_BAR_DISMISSED_KEY)
	);

	// The Knowledge DB page shows its own sticky "Looks good" return bar while the
	// tour hands the user over (`?tour=1`). Suppress this generic reminder there so
	// the two bottom bars don't stack/overlap — it reappears once the user leaves.
	const isKnowledgeDbReturn =
		location.pathname === ROUTE_CONFIG.KNOWLEDGE_DB &&
		searchParams.get(KNOWLEDGE_DB_TOUR_PARAM) === "1";

	const status = tourState?.status ?? null;
	const isTourInactive = status === OnboardingTourStatus.DONE;
	const skip = !user?.isOnboarded || isTourInactive;
	const shouldPoll = !skip && !isTourOpen && tourState !== null && hasPendingGeneration(tourState);

	useGetOnboardingTourQuery(undefined, {
		skip,
		pollingInterval: shouldPoll ? POLL_INTERVAL_MS : 0,
	});
	const [retryKnowledgeBase, { isLoading: isRetrying }] = useRetryKnowledgeBaseMutation();

	// Hidden while either tour overlay is open (it reappears when they close) and
	// while the Knowledge DB return bar owns the bottom of the screen.
	if (!tourState || skip || isTourOpen || isListTourOpen || isKnowledgeDbReturn) {
		return null;
	}

	if (
		tourState.status !== OnboardingTourStatus.ACTIVE &&
		tourState.status !== OnboardingTourStatus.SKIPPED
	) {
		return null;
	}

	const readyStep = getReadyStep(tourState);
	const failedStep = readyStep ? null : getFailedStep(tourState);
	// A failed build is keyed apart from a ready one, so hiding "ready" never hides "failed".
	const surfacedKey = readyStep ?? (failedStep ? `failed:${failedStep}` : null);

	if (!surfacedKey || surfacedKey === dismissedStep) {
		return null;
	}

	// The INVITED "Welcome tour" only *shows* the shared workspace settings — an
	// invited user must not be nudged to review (let alone change) a lead scoring
	// their team owns, so the reminder never fires for them.
	if (tourState.variant === OnboardingTourVariant.INVITED) {
		return null;
	}

	const dismiss = () => {
		sessionStorage.setItem(TOUR_READY_BAR_DISMISSED_KEY, surfacedKey);
		setDismissedStep(surfacedKey);
	};

	return (
		// z stays below the sidebar (z-[200]) so the full-bleed bar tucks behind it
		// instead of covering its bottom nav items — and below the tour modal
		// (z-[99990]) and the MUI modal layer (~1000+). Still above the content
		// outlet (z-10).
		<div className="fixed bottom-0 inset-x-0 z-[150] bg-white border-t border-border-light shadow-lg pl-4 pr-12 py-2.5">
			<div className="flex items-center justify-center gap-3 flex-wrap">
				{readyStep ? (
					<>
						<Icon className="w-4 h-4 text-brand-light shrink-0" icon={faSparkles} />
						<span className="text-sm text-text">
							{t(`onboardingTour.readyBar.${READY_BAR_COPY_KEY[readyStep]}`)}
						</span>
						<Button
							size={ButtonSize.XS}
							title={t("onboardingTour.readyBar.cta")}
							type="button"
							onClick={() => dispatch(openTour(readyStep))}
						/>
					</>
				) : (
					<>
						<Icon className="w-4 h-4 text-red-600 shrink-0" icon={faTriangleExclamation} />
						<span className="text-sm text-text" data-testid="tour-ready-bar-failed">
							{t("onboardingTour.readyBar.knowledgeFailed")}
						</span>
						<Button
							isLoading={isRetrying}
							size={ButtonSize.XS}
							title={t("onboardingTour.readyBar.retry")}
							type="button"
							onClick={() => void retryKnowledgeBase()}
						/>
					</>
				)}
			</div>
			{/* absolute so the message stays optically centred in the bar */}
			<button
				aria-label={t("onboardingTour.readyBar.dismiss")}
				className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer rounded-lg p-1.5 text-text-tertiary duration-200 hover:bg-background-light hover:text-text"
				title={t("onboardingTour.readyBar.dismiss")}
				type="button"
				onClick={dismiss}
			>
				<Icon className="w-4 h-4" icon={faXmark} />
			</button>
		</div>
	);
};
